import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDown, CheckCircle2, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { type FieldValues, type Resolver, type SubmitHandler, type UseFormRegister, useForm } from "react-hook-form";
import { useLocation, useParams } from "react-router-dom";
import { z } from "zod";
import { isLocale, localizedPath } from "../../lib/routes";
import { track } from "../../lib/analytics";
import { submitLead } from "../../services/leadService";
import { Button } from "../ui/Button";

const schema = z.object({
  companyName: z.string().trim().min(2, "Company name is required").max(160),
  website: z.string().trim().min(1, "Website is required").max(200),
  contactName: z.string().trim().min(2, "Your name is required").max(120),
  role: z.string().trim().min(2, "Role is required").max(120),
  email: z.string().trim().email("Valid email is required").max(160),
  phone: z.string().trim().max(80).optional(),
  problem: z.string().trim().min(12, "Describe the problem your technology solves").max(2000),
  category: z.string().min(1, "Technology category is required"),
  commercialStage: z.string().min(1, "Commercial stage is required"),
  typicalCustomer: z.string().trim().min(12, "Describe your typical customer").max(1200),
  locations: z.string().trim().min(2, "Where is the technology operating today?").max(600),
  outsideIsrael: z.string().min(1, "Select an option"),
  englishDocs: z.string().min(1, "Select an option"),
  localInstall: z.string().min(1, "Select an option"),
  localService: z.string().min(1, "Select an option"),
  exploring: z.array(z.string()).min(1, "Select at least one").default([]),
  exploredBefore: z.string().min(1, "Select an option"),
  exploredNote: z.string().trim().max(1200).optional(),
  whatWouldMakeInteresting: z.string().trim().min(12, "Please describe").max(3000),
  useCase: z.string().trim().max(1500).optional(),
  consent: z.boolean().refine(Boolean, "Consent is required"),
  honeypot: z.string().max(0).optional()
});

const steps = ["Your company", "Your technology", "International readiness", "Why Moldova?", "What would make Moldova interesting?"];
const stepFields: string[][] = [
  ["companyName", "website", "contactName", "role", "email"],
  ["problem", "category", "commercialStage", "typicalCustomer"],
  ["locations", "outsideIsrael", "englishDocs", "localInstall", "localService"],
  ["exploring", "exploredBefore"],
  ["whatWouldMakeInteresting", "consent"]
];

const categories = ["Energy storage", "Agrivoltaics", "Industrial energy", "Water technology", "AgriTech", "Climate adaptation", "Energy management", "Other"];
const stages = ["Commercial deployments", "Paid pilots", "Demonstration stage", "Pre-commercial"];
const yesNo = ["Yes", "No"];
const yesNoNotSure = ["Yes", "No", "Not sure"];
const englishDocs = ["Yes", "Partly", "No"];
const exploringOptions = ["New customers", "Pilot deployment", "Distribution partner", "Strategic partner", "EU market expansion", "Financing / funded demonstration", "Local manufacturing or assembly", "Market research", "Other"];

function Field({ label, name, register, error, optional = false, type = "text", textarea = false, helper }: { label: string; name: string; register: UseFormRegister<FieldValues>; error?: string; optional?: boolean; type?: string; textarea?: boolean; helper?: string }) {
  return (
    <label className="grid gap-2 text-sm font-semibold">
      <span>{label}{optional ? <span className="font-normal text-ink-secondary"> (Optional)</span> : <span className="text-brand-error"> *</span>}</span>
      {textarea ? (
        <textarea className="min-h-28 w-full rounded-xl border border-line bg-surface px-3.5 py-2.5 text-[0.95rem] leading-7 transition-colors hover:border-brand-blue/40 focus:border-brand-blue" {...register(name)} aria-invalid={Boolean(error)} />
      ) : (
        <input className="min-h-11 w-full rounded-lg border border-line bg-surface px-3.5 py-2.5 text-[0.95rem] transition-colors hover:border-brand-blue/40 focus:border-brand-blue" type={type} {...register(name)} aria-invalid={Boolean(error)} />
      )}
      {helper && !error && <span className="text-xs font-normal leading-5 text-ink-secondary">{helper}</span>}
      {error && <span className="text-sm font-medium text-brand-error">{error}</span>}
    </label>
  );
}

function SelectField({ label, name, options, register, error }: { label: string; name: string; options: string[]; register: UseFormRegister<FieldValues>; error?: string }) {
  return (
    <label className="grid gap-2 text-sm font-semibold">
      <span>{label} <span className="text-brand-error">*</span></span>
      <span className="relative block">
        <select className="min-h-11 w-full appearance-none rounded-lg border border-line bg-surface px-3.5 py-2.5 pr-10 text-[0.95rem] transition-colors hover:border-brand-blue/40 focus:border-brand-blue" {...register(name)} aria-invalid={Boolean(error)}>
          <option value=""></option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-secondary" aria-hidden="true" />
      </span>
      {error && <span className="text-sm font-medium text-brand-error">{error}</span>}
    </label>
  );
}

export function IsraelAssessmentForm() {
  const { locale } = useParams();
  const location = useLocation();
  const activeLocale = isLocale(locale) ? locale : "en";
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [startedAt] = useState(Date.now());
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const storageKey = "ptd:israel:assessment";
  const form = useForm<FieldValues>({
    resolver: zodResolver(schema) as unknown as Resolver<FieldValues>,
    mode: "onBlur",
    defaultValues: useMemo(() => { try { return JSON.parse(localStorage.getItem(storageKey) || "{}"); } catch { return {}; } }, [storageKey])
  });
  const register = form.register;

  useEffect(() => {
    const sub = form.watch((value) => localStorage.setItem(storageKey, JSON.stringify({ ...value, consent: false })));
    return () => sub.unsubscribe();
  }, [form, storageKey]);

  useEffect(() => { track("israel_assessment_started", { locale: activeLocale }); }, [activeLocale]);

  const onSubmit: SubmitHandler<FieldValues> = async (values) => {
    setStatus("submitting");
    try {
      await submitLead({
        leadType: "israel_technology",
        locale: activeLocale,
        sourcePage: location.pathname,
        consent: Boolean(values.consent),
        startedAt,
        honeypot: values.honeypot,
        data: values as Record<string, unknown>
      });
      track("israel_assessment_completed", { locale: activeLocale });
      localStorage.removeItem(storageKey);
      form.reset();
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-2xl border border-line bg-surface p-8 shadow-soft">
        <CheckCircle2 className="mb-4 h-8 w-8 text-brand-success" aria-hidden="true" />
        <h3 className="mb-3 text-2xl font-bold">Thank you.</h3>
        <p className="max-w-2xl leading-7 text-ink-secondary">We review the opportunity across four dimensions:</p>
        <ul className="mt-4 grid max-w-xl gap-2 text-sm font-semibold">
          {["Technology readiness", "Market problem", "Economics", "Localization"].map((item) => (
            <li key={item} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-cyan" aria-hidden="true" />{item}</li>
          ))}
        </ul>
        <p className="mt-4 max-w-2xl leading-7 text-ink-secondary">You will receive one of four recommendations: <span className="font-bold text-ink-primary">Go · Refine · Wait · Stop</span></p>
        <p className="mt-3 max-w-2xl leading-7 text-ink-secondary">If we see a credible hypothesis worth exploring, we’ll suggest the most useful next step.</p>
        <div className="mt-7 flex flex-col gap-3 sm:flex-row">
          <Button to={localizedPath(activeLocale, "israel")}>Back to Israel page</Button>
          <Button variant="secondary" to={localizedPath(activeLocale, "contact")}>Talk to TransitionBridge</Button>
        </div>
      </div>
    );
  }

  if (!open) {
    return (
      <div>
        <Button type="button" size="lg" onClick={() => { setOpen(true); setStep(0); }}>Start assessment</Button>
      </div>
    );
  }

  const errors = form.formState.errors as Record<string, { message?: string }>;

  return (
    <form className="rounded-2xl border border-line bg-surface p-4 shadow-soft md:p-8" onSubmit={form.handleSubmit(onSubmit)} noValidate>
      <div className="mb-8">
        <div className="mb-3 flex items-center justify-between gap-3 text-sm font-semibold">
          <span>{steps[step]}</span>
          <span>{step + 1} / {steps.length}</span>
        </div>
        <div className="h-1.5 rounded-full bg-line/80">
          <div className="h-1.5 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan transition-all duration-300" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>
        <p className="mt-3 text-sm text-ink-secondary">Draft saved on this device.</p>
      </div>

      <input className="hidden" tabIndex={-1} autoComplete="off" {...form.register("honeypot")} aria-label="Leave this field empty." />

      <p className="mb-6 text-sm text-ink-secondary">We only ask for information needed to assess market fit.</p>

      <div className="grid gap-5 md:grid-cols-2">
        {step === 0 && (
          <>
            <Field label="Company name" name="companyName" register={register} error={errors.companyName?.message} />
            <Field label="Website" name="website" register={register} error={errors.website?.message} />
            <Field label="Your name" name="contactName" register={register} error={errors.contactName?.message} />
            <Field label="Role" name="role" register={register} error={errors.role?.message} />
            <Field label="Email" name="email" type="email" register={register} error={errors.email?.message} />
            <Field label="Phone" name="phone" register={register} error={errors.phone?.message} optional />
          </>
        )}
        {step === 1 && (
          <>
            <Field label="What problem does your technology solve?" name="problem" register={register} error={errors.problem?.message} textarea />
            <SelectField label="Technology category" name="category" options={categories} register={register} error={errors.category?.message} />
            <SelectField label="Current commercial stage" name="commercialStage" options={stages} register={register} error={errors.commercialStage?.message} />
            <Field label="Briefly describe your typical customer" name="typicalCustomer" register={register} error={errors.typicalCustomer?.message} textarea />
          </>
        )}
        {step === 2 && (
          <>
            <Field label="Where is the technology operating today?" name="locations" register={register} error={errors.locations?.message} textarea />
            <SelectField label="Do you already have deployments outside Israel?" name="outsideIsrael" options={yesNo} register={register} error={errors.outsideIsrael?.message} />
            <SelectField label="Is technical documentation available in English?" name="englishDocs" options={englishDocs} register={register} error={errors.englishDocs?.message} />
            <SelectField label="Does deployment require local installation?" name="localInstall" options={yesNoNotSure} register={register} error={errors.localInstall?.message} />
            <SelectField label="Does the technology require ongoing local service?" name="localService" options={yesNoNotSure} register={register} error={errors.localService?.message} />
          </>
        )}
        {step === 3 && (
          <>
            <div className="md:col-span-2">
              <h3 className="mb-3 font-semibold"><span className="text-brand-error">*</span> What are you currently exploring?</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {exploringOptions.map((item) => (
                  <label key={item} className="flex min-h-11 items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2 text-sm font-semibold">
                    <input type="checkbox" value={item} {...form.register("exploring")} />
                    {item}
                  </label>
                ))}
              </div>
              {errors.exploring?.message && <p className="mt-3 text-sm font-medium text-brand-error">{errors.exploring.message}</p>}
            </div>
            <SelectField label="Have you previously explored Moldova or Romania?" name="exploredBefore" options={yesNo} register={register} error={errors.exploredBefore?.message} />
            <Field label="If yes, tell us briefly what happened" name="exploredNote" register={register} error={errors.exploredNote?.message} textarea optional />
          </>
        )}
        {step === 4 && (
          <>
            <Field label="What would need to be true for Moldova to become a meaningful market for your company?" name="whatWouldMakeInteresting" register={register} error={errors.whatWouldMakeInteresting?.message} textarea />
            <Field label="Is there a specific use case or customer type you would like us to examine?" name="useCase" register={register} error={errors.useCase?.message} textarea optional />
            <label className="flex items-start gap-3 rounded-xl border border-line bg-surface p-4 text-sm font-semibold md:col-span-2">
              <input className="mt-1" type="checkbox" {...form.register("consent")} />
              <span>I agree that TransitionBridge may contact me regarding this assessment.</span>
            </label>
            {errors.consent?.message && <p className="text-sm font-medium text-brand-error md:col-span-2">{errors.consent.message}</p>}
            <div className="flex items-center gap-2 text-sm text-ink-secondary md:col-span-2">
              <ShieldCheck className="h-4 w-4 text-brand-success" aria-hidden="true" />
              <span>Your information is used only to review this assessment.</span>
            </div>
          </>
        )}
      </div>

      {status === "error" && <p className="mt-5 rounded-lg border border-brand-error/30 bg-white p-3 text-sm font-medium text-brand-error">We could not submit right now. Please review the fields or try again later.</p>}

      <div className={`mt-8 flex flex-col gap-3 sm:flex-row ${step > 0 ? "sm:justify-between" : "sm:justify-end"}`}>
        {step > 0 && (
          <Button variant="secondary" arrow={false} disabled={status === "submitting"} onClick={() => setStep((v) => Math.max(0, v - 1))}>Back</Button>
        )}
        {step < steps.length - 1 ? (
          <Button type="button" onClick={async () => {
            const ok = await form.trigger(stepFields[step]);
            if (ok) setStep((v) => Math.min(steps.length - 1, v + 1));
          }}>Continue</Button>
        ) : (
          <Button type="submit" className="sm:min-w-64">{status === "submitting" ? "Submitting..." : "Submit for review"}</Button>
        )}
      </div>
    </form>
  );
}
